import { PlusIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ComparisonDimension } from "#shared/comparisonDimension.ts";
import type { ComparisonBreakdown } from "#shared/comparisonSelection.ts";
import { Button } from "@/components/ui/button.tsx";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";
import { BreakdownButton } from "./BreakdownButton.tsx";
import { CapTooltip } from "@packages/core-client/components/CapTooltip.tsx";

export interface ComparisonRowActionsProps {
    nodeId: string;
    label: string;
    breakdown: ComparisonBreakdown | undefined;
    maxBreakdownSelections: number;
    usedSeriesCount: number;
    maxSeriesCount: number;
    onDuplicate?: (nodeId: string) => void;
    onBreakdownChange: (nodeId: string, dimension: ComparisonDimension, groupKeys: string[]) => void;
    onRemove?: (nodeId: string) => void;
}

export function ComparisonRowActions({ nodeId, label, breakdown, maxBreakdownSelections, usedSeriesCount, maxSeriesCount, onDuplicate, onBreakdownChange, onRemove }: ComparisonRowActionsProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ComparisonRowActions", { i18n: i18nextInstance });

    const isFull = usedSeriesCount >= maxSeriesCount;

    const handleDuplicate = useCallback(() => !isFull && onDuplicate?.(nodeId), [isFull, onDuplicate, nodeId]);
    const handleRemove = useCallback(() => onRemove?.(nodeId), [onRemove, nodeId]);

    return (
        <>
            {onDuplicate && (
                <CapTooltip isShown={isFull} label={t("capReached", { count: maxSeriesCount })}>
                    <Button intent="plain" size="sm" className={`text-muted-fg hover:text-fg h-9 ${isFull ? "opacity-50" : ""}`} aria-disabled={isFull} onPress={handleDuplicate}>
                        <PlusIcon aria-hidden="true" />
                        {t("addComparison")}
                    </Button>
                </CapTooltip>
            )}

            <BreakdownButton nodeId={nodeId} breakdown={breakdown} maxSelections={maxBreakdownSelections} usedSeriesCount={usedSeriesCount} maxSeriesCount={maxSeriesCount} onBreakdownChange={onBreakdownChange} />

            {onRemove && (
                <Button intent="plain" size="sq-md" className="h-9" aria-label={t("remove", { label })} onPress={handleRemove}>
                    <XMarkIcon className="size-4" aria-hidden="true" />
                </Button>
            )}
        </>
    );
}
